import { useApiClient } from "@positive/api-client";
import { useFormatter } from "@positive/ui";
import { Button, MessageStrip } from "@ui5/webcomponents-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useIdentity } from "./IdentityProvider";
import type { Impersonation } from "./session";
import { clearSupportSession } from "./supportSession";

/**
 * Operator support session banner (A3, ADR-0003/D-8): always visible while an operator acts in the tenant, with the
 * operator, read-only flag and expiry. Ending it closes the support session and signs the operator out.
 */
export function ImpersonationBanner({ impersonation }: { impersonation: Impersonation }) {
  const { t } = useTranslation("shell");
  const formatter = useFormatter();
  const client = useApiClient();
  const { auth, session } = useIdentity();
  const [ending, setEnding] = useState(false);

  const end = async () => {
    setEnding(true);
    try {
      await client.post(`platform/admin/support-sessions/${encodeURIComponent(impersonation.sessionId)}/end`);
    } catch {
      // The session expires on its own; signing out still ends this browser's use of it.
    } finally {
      clearSupportSession(window.sessionStorage);
      await auth.signOut();
    }
  };

  return (
    <MessageStrip
      design="Critical"
      hideCloseButton
      className="positive-impersonation-banner"
      data-testid="impersonation-banner"
    >
      {t(impersonation.readOnly ? "impersonation.readOnly" : "impersonation.actingAs", {
        operator: impersonation.operator,
        user: session?.user.displayName ?? "",
        until: formatter.dateTime(impersonation.expiresAt),
      })}{" "}
      <Button design="Transparent" disabled={ending} onClick={() => void end()} data-testid="impersonation-end">
        {t("impersonation.end")}
      </Button>
    </MessageStrip>
  );
}
